import { useCallback, useEffect, useMemo, useState, ChangeEvent, KeyboardEvent, FocusEvent } from 'react';
import { P2Button } from 'react-bootstrap-common-script';
import { DropdownItemInterface } from '../../models/interfaces/dropdown-item.interface';
import data from "../../../src/utils/shared/dummy.json";
import { authenticateUserByNRIC, authenticateUserBySingpassToken, getSpcpName, resetAuthData, updateExpiredIn, updateSessionTimeout, updateSingpassToken } from '../../redux/slices/authentication-slice';
import { useAppDispatch } from '../../redux/hook';
import config from '../../config';
import moment from 'moment';
import { LottieLoader } from '../../components/loader';
import { PolymerAxios } from '../../utils/axios';
import { API_PurgedAllData, API_PurgedData } from '../../utils/api';
import SuggestionsList from '../../pages/authentication/SuggestionsList';
import { useNavigate } from 'react-router-dom';

function NormalApiLogin() {

  const [dropdownOptions, setDropdownOptions] = useState<any[]>([]);
  useEffect(() => {
    const array: any[] = [];

    let index = 0;
    const processedDropdown: any[] = [];
    while (index < array.length) {
      for(const owner of array[index].owners) {
        processedDropdown.push({
          id: processedDropdown.length + 1,
          title: `${array[index].hdbRef} - ${owner}`,
          value: owner,
          code: false,
          hide: false,
          dynamic: true,
          login_type: "SP",
        });
      }
      index++;
    }

   setDropdownOptions([...processedDropdown, ...data.corppassMyInfo])//change to data.singpassMyInfo to use hardcoded data from json
  }, []);

  const isProd = useMemo(()=>{
    console.log("process.env.NODE_ENV",process.env.NODE_ENV);
    if (process.env.NODE_ENV !== "production" || document.location.origin === "https://services3-dev.hdb.gov.sg" || document.location.origin === "https://services3-stg.hdb.gov.sg") {
      return false;
    }
    else{
      return true;
    }
  },[])

  const purgeData = useCallback(()=>{
    dropdownOptions.filter(e=>!e.hide).forEach(async e=>{
      await PolymerAxios('', config.byPassSingpassToken).delete(`${API_PurgedData}/${e.value}`,{
        headers:{
          "x-purge-key": "XNSCszfxXkcbQoP",
        }
      })
    })
  },[dropdownOptions])
  const purgeAllData = useCallback(()=>{
    PolymerAxios('', config.byPassSingpassToken).delete(`${API_PurgedAllData}`,{
      headers:{
        "x-purge-key": "XNSCszfxXkcbQoP",
      }
    })
  },[])

  const [nric, setNric] = useState<string>("");
  const [filteredSuggestions, setFilteredSuggestions] = useState<{ title: string, value: string }[]>([]);
  const [activeSuggestion, setActiveSuggestion] = useState<number>(0);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<DropdownItemInterface | null>(null); // New state variable for selected suggestion

  const queryParameters = new URLSearchParams(window.location.search);
  const singpass_code = queryParameters.get("code");
  const expires_in = queryParameters.get("expires_in");
  const AppDispatch = useAppDispatch();
  const navigate = useNavigate(); 

  const [ isProcess, setIsProcess ] = useState<boolean>(false);

  // CRITICAL FIX: When a fresh login code is present in the URL, immediately clear
  // any stale persisted auth data BEFORE any API calls are made.
  // This prevents the redirect loop caused by using old tokens.
  useEffect(() => {
    if (singpass_code && expires_in) {
      console.log('[AUTH] Fresh login code detected, clearing stale auth data');
      
      // Clear stale token from localStorage (Redux persist key)
      try {
        const persistAuthKey = 'persist:auth';
        const oldAuthData = localStorage.getItem(persistAuthKey);
        if (oldAuthData) {
          try {
            const parsed = JSON.parse(oldAuthData);
            const authSlice = JSON.parse(parsed.AuthenticationSlice || '{}');
            // Only clear if there's a different (stale) token
            if (authSlice.singpassToken && authSlice.singpassToken !== singpass_code) {
              console.log('[AUTH] Found stale token, clearing persist:auth');
              localStorage.removeItem(persistAuthKey);
            }
          } catch (e) {
            // If parsing fails, clear anyway to be safe
            localStorage.removeItem(persistAuthKey);
          }
        }
      } catch (e) {
        console.error('[AUTH] Error clearing localStorage:', e);
      }
      
      // Clear any stale token from Redux state
      AppDispatch(updateSingpassToken(""));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Run only once on mount - intentionally not including dependencies

  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);

  const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const userInput = e.currentTarget.value;
    setNric(userInput);
    setActiveSuggestion(0); // Add this line to reset active suggestion

    setIsButtonDisabled(userInput.trim() === "");
  }, [filteredSuggestions]);

  const updateSuggestions = useCallback(() => {
    const suggestions: {title: string, value: string}[] = dropdownOptions.filter(
      suggestion => !suggestion.hide && (nric.length > 0 ? suggestion.title.toLowerCase().includes(nric.toLowerCase()) : true)
    ).map(suggestion => ({
      title: suggestion.title,
      value: suggestion.value
    }));

    setFilteredSuggestions(suggestions)
  }, [nric, dropdownOptions])

  useEffect(() => {
    document.onclick = (e) => {
      const stopClosingClasses = ["polymer-normal-login-input", "polymer-normal-login-ul", "polymer-normal-login-li"];
      const target = e.target as HTMLElement;
      console.log(target.className)
      if(!stopClosingClasses.includes(target.className)){
         setShowSuggestions(false);
      }
    }

    return () => {
      document.onclick = null;
    }
  }, [showSuggestions])

  useEffect(() => {
     updateSuggestions();
  }, [dropdownOptions])

  useEffect(() => {
     updateSuggestions();
  }, [nric])

  const onFocus = useCallback(() => {
    setShowSuggestions(true); // Show suggestions when input is focused
    setActiveSuggestion(0); // Reset active suggestion index
  }, []);

  const onClick = (suggestion: { title: string, value: string }) => {
    console.log('suggestionTitle', suggestion.title);
    setSelectedSuggestion(dropdownOptions.find(item => item.value === suggestion.value) || null);
    setNric(suggestion.title); // Update the input to show the title
    setShowSuggestions(false);
    setIsButtonDisabled(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setSelectedSuggestion(dropdownOptions.find(item => item.value === filteredSuggestions[activeSuggestion].value) || null);
      setNric(filteredSuggestions[activeSuggestion].title);
      setFilteredSuggestions([]);
      setShowSuggestions(false);
      setIsButtonDisabled(false);
    } else if (e.key === 'ArrowUp') {
      if (activeSuggestion === 0) return;
      setActiveSuggestion(activeSuggestion - 1);
    } else if (e.key === 'ArrowDown') {
      if (activeSuggestion === filteredSuggestions.length - 1) return;
      setActiveSuggestion(activeSuggestion + 1);
    }
  };

  const isSingpassUser = useMemo(() => {
    return dropdownOptions.find(e => e.value === selectedSuggestion?.value && e.hide === false)?.code;
  }, [selectedSuggestion, dropdownOptions]);

  //Authenticate Login
  const authenticateLogin = useCallback(async () => {
    if (!isSingpassUser) {
      // Determine the NRIC/UINFIN to use for login
      const uinfin = selectedSuggestion?.value ?? nric;
      const selectedOption = dropdownOptions.find((e) => e.value === uinfin);
      console.log('[Login Attempt] Login Type:', selectedOption?.login_type || 'Unknown');

      AppDispatch(updateSingpassToken(selectedOption?.login_type === 'CP' ? "MOCK_BEARER_CP" : config.byPassSingpassToken));
      AppDispatch(updateExpiredIn(moment().add(30, 'minutes').unix()));
      AppDispatch(updateSessionTimeout(moment().add(25, 'minutes').unix()));
      AppDispatch(authenticateUserByNRIC({ uinfin, loginOptions: dropdownOptions }));
    } else {
      window.location.href = `${config.singpassLoginUrl}`;
      return null;
    }
  }, [AppDispatch, isSingpassUser, nric, selectedSuggestion, dropdownOptions]);

  const authenticateSingpass = useCallback(() => {
    window.location.href = `${config.singpassLoginUrl}`;
    return null;
  }, [])

  const resetLogin = useCallback(()=>{
    setIsProcess(false);
    AppDispatch(resetAuthData());

    if(isProd){
      window.location.href = `${config.singpassLoginUrl}`;
    }
  },[AppDispatch, isProd])


  useEffect(()=>{
    if(isProd){
      if(singpass_code && expires_in){
        // has singpass code,
        // validate by another use effect
        // do nothing
      }
      else{
        window.location.href = `${config.singpassLoginUrl}`;
      }
    }
    else{
      //do nothing
    }

  },[expires_in, isProd, singpass_code])

  //WHEN CODE AND EXPIRY RETURN TO THIS PAGE, WE WILL AUTHENTICATED IS TRUE
  useEffect(()=>{
    if (singpass_code && expires_in) {
      setIsProcess(true);
    }

    // Get retry count from sessionStorage, default to 0
    let storedRetryCount = Number(sessionStorage.getItem('spRetryCount') || 0);
    const lastRetry = sessionStorage.getItem('spLastRetryTime');

    // Allow the 3 retries to happen if last time was > 5 seconds ago
    if (lastRetry) {
      const lastRetryTime = parseInt(lastRetry, 10);
      const now = Date.now();
      const fiveSeconds = 5 * 1000;

      if (now - lastRetryTime >= fiveSeconds) {
        storedRetryCount = 0;
        sessionStorage.removeItem('spRetryCount');
        sessionStorage.removeItem('spLastRetryTime');
      }
    }

    if (storedRetryCount >= 3) {
      console.warn('Max retries reached. Stopping authentication attempts.');
      navigate('/maintenance');
      return;
    }

    let timeoutId: number;
    timeoutId = window.setTimeout(() => {
      if(singpass_code && expires_in){
        AppDispatch(authenticateUserBySingpassToken({ singpass_code })).then(res => {
          //NOTE : authenticateUserBySingpassToken will validate the singpass code and directly update the myinfo slice in the authentication-slice.tsx. then here is to only check if its fulfilled or reject
          console.log("authenticateUserBySingpassToken",singpass_code, res);
          console.log("expires_in: ",(Number(expires_in))/1000);
          if (res.type === "authenticateUserBySingpassToken/fulfilled") {
            AppDispatch(updateSingpassToken(singpass_code ?? ""));
            AppDispatch(updateExpiredIn((Number(expires_in))/1000));
            AppDispatch(updateSessionTimeout(((Number(expires_in))/1000) - 300));
            sessionStorage.removeItem('spRetryCount'); // clear retries on success
            sessionStorage.removeItem('spLastRetryTime');
          }
          else {
            const currentRetry = Number(sessionStorage.getItem('spRetryCount') || 0);
            sessionStorage.setItem('spRetryCount', String(currentRetry + 1));
            sessionStorage.setItem('spLastRetryTime', Date.now().toString());
            resetLogin();
          }
        }).catch(e => {
          const currentRetry = Number(sessionStorage.getItem('spRetryCount') || 0);
          sessionStorage.setItem('spRetryCount', String(currentRetry + 1));
          sessionStorage.setItem('spLastRetryTime', Date.now().toString());
          resetLogin();
        });

        AppDispatch(getSpcpName({singpass_code})).then(res => {
          console.log("getSpcpName res: ",res);
        });
      }
      else{
        resetLogin();
      }
    }, 200);
    return () => {
      clearTimeout(timeoutId);
    }
  },[AppDispatch, expires_in, resetLogin, singpass_code]);

  if(isProcess){
    return <>
      <LottieLoader/>
    </>
  }


  if(isProd){
    return <>
      <LottieLoader/>
    </>
  }


  return (
    <main className='container containerPadding' style={{display: "flex", flexDirection: "column", marginBottom: '70px' }}>
      <h1>Normal Login Page</h1>
      <div style={{position: 'relative'}}>
        <input
          type="text"
          className="polymer-normal-login-input"
          value={nric}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          placeholder="Please select"
          style={{ 
            marginBottom: -4, 
            marginTop: 10, 
            padding: 10, 
            border: "1px solid #0C8188", 
            borderRadius: 8, 
            outline: 'none',
            width: '100%'
          }}
        />
        {showSuggestions && (
          <SuggestionsList
            filteredSuggestions={filteredSuggestions}
            activeSuggestion={activeSuggestion}
            onClick={onClick}
          />
        )}
      </div>
      <div style={{display: 'flex', justifyContent: 'space-between'}}>
        <P2Button style={{ margin: "20px 0" }} onClick={authenticateLogin} variant={"outline-primary"} disabled={isButtonDisabled}>Login</P2Button>
        <P2Button style={{ margin: "20px 0", backgroundColor: '#f5333d', border: 'solid 1px #f5333d', fontWeight: 'bold', padding: "0 25px" }} onClick={authenticateSingpass} variant={"primary"}>Login with Singpass</P2Button>
      </div>
      <p hidden onClick={purgeData}>P.Data</p>
      <p hidden onClick={purgeAllData}>P.All.Data</p>
    </main>
  )
}

export default NormalApiLogin
